import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { filesApi } from "@/api/files-api";
import { workspaceApi } from "@/api/workspace-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { COVER_PRESETS, coverStyle } from "@/pages/raise-board/rb-ui";
import type { IWorkspace } from "@/types/data-models";

const HEX = /^#(?:[0-9a-f]{3}){1,2}$/i;
const BANNER_FILE_TYPE = "company banner";
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * "Public page header": the ground the workspace's portal, raiseboard and
 * lookbooks open on. A preset, any custom colour, and optionally an image
 * (tinted by the colour so white type always reads).
 */
export function PublicCoverSettings({ workspace }: { workspace: IWorkspace }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [color, setColor] = useState<string | null>(workspace.cover_color ?? null);
  const [hexDraft, setHexDraft] = useState(workspace.cover_color ?? "");
  const [imageUrl, setImageUrl] = useState<string | null>(workspace.background_cover ?? null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setColor(workspace.cover_color ?? null);
    setHexDraft(workspace.cover_color ?? "");
    setImageUrl(workspace.background_cover ?? null);
  }, [workspace.cover_color, workspace.background_cover]);

  const dirty =
    (color ?? null) !== (workspace.cover_color ?? null) ||
    (imageUrl ?? null) !== (workspace.background_cover ?? null);

  const pick = (next: string | null) => {
    setColor(next);
    setHexDraft(next ?? "");
  };

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choose an image file (JPG, PNG or WebP).");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Images up to 5MB, please.");
      return;
    }
    setUploading(true);
    try {
      const response = await filesApi.uploadFile(
        {
          file,
          file_name: file.name || "cover.jpg",
          file_type: BANNER_FILE_TYPE,
          object_ref: "company",
          object_ref_value: workspace.affiliated_company?._id || "",
        } as Parameters<typeof filesApi.uploadFile>[0],
        workspace.affiliated_company?._id,
        workspace._id
      );
      const url = response.result?.file_url;
      if (!url) throw new Error("The upload didn't return an address");
      setImageUrl(url);
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't upload that image"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await workspaceApi.updatePublicCover(workspace._id, {
        cover_color: color,
        background_cover: imageUrl,
      });
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace._id] });
      // Public pages read their own copies of the workspace.
      queryClient.invalidateQueries({ queryKey: ["lookbook"] });
      queryClient.invalidateQueries({ queryKey: ["raise-board"] });
      toast.success("Header updated on your public pages");
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't save the header"));
    } finally {
      setSaving(false);
    }
  };

  const fundName = workspace.opportunity_name || "Your fund";

  return (
    <section className="mt-8">
      <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
        Public page header
      </h2>
      <div className="rounded-lg border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">
          The background your portal, Raiseboard and lookbooks open on.
        </p>

        {/* Live preview, drawn with the same style the public pages use. */}
        <div
          className="public-theme ink-scope mt-4 overflow-hidden rounded-md bg-background px-5 py-6"
          style={coverStyle({ color, imageUrl })}
          aria-label="Header preview"
        >
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Raiseboard
          </p>
          <p className="mt-1.5 text-2xl font-semibold tracking-[-0.03em] text-foreground">
            {fundName}
          </p>
        </div>

        <div className="mt-4">
          <Label className="text-xs text-muted-foreground">Colour</Label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {COVER_PRESETS.map((preset) => {
              const active = (preset.color ?? null) === (color ?? null);
              return (
                <button
                  key={preset.name}
                  type="button"
                  title={preset.name}
                  aria-label={`${preset.name} header`}
                  aria-pressed={active}
                  onClick={() => pick(preset.color)}
                  className={cn(
                    "public-theme ink-scope relative h-8 w-8 rounded-md border border-border bg-background transition-transform duration-150 hover:scale-105",
                    active && "ring-2 ring-foreground ring-offset-2 ring-offset-card"
                  )}
                  style={coverStyle({ color: preset.color }) ?? undefined}
                >
                  {active && <Check className="absolute inset-0 m-auto h-3.5 w-3.5 text-white" />}
                </button>
              );
            })}

            <div className="ml-1 flex items-center gap-2">
              <input
                type="color"
                aria-label="Custom header colour"
                value={color && HEX.test(color) && color.length === 7 ? color : "#14213d"}
                onChange={(e) => pick(e.target.value)}
                className="h-8 w-8 cursor-pointer rounded-md border border-border bg-transparent p-0.5"
              />
              <Input
                aria-label="Header colour hex"
                value={hexDraft}
                placeholder="#14213d"
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setHexDraft(v);
                  if (HEX.test(v)) setColor(v.toLowerCase());
                }}
                className="h-8 w-28 font-mono text-xs"
              />
            </div>
          </div>
        </div>

        <div className="mt-5">
          <Label className="text-xs text-muted-foreground">Image (optional)</Label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading || !workspace.affiliated_company?._id}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              {imageUrl ? "Replace image" : "Upload image"}
            </Button>
            {imageUrl && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setImageUrl(null)}
                className="text-muted-foreground"
              >
                <X className="h-4 w-4" />
                Remove image
              </Button>
            )}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            A wide photo works best. It sits under a tint of your colour so the
            text stays readable.
          </p>
        </div>

        <div className="mt-5 flex justify-end">
          <Button type="button" size="sm" disabled={!dirty || saving || uploading} onClick={save}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save header
          </Button>
        </div>
      </div>
    </section>
  );
}
